<script lang="ts">
	import type { Coordinates } from '$lib/components/map/types';

	import type { GetYourGuideTour } from '../../types';
	import { browser } from '$app/environment';
	import NoActivity from '$assets/pois/activity.webp';
	import DefaultGetYourGuide from '$assets/pois/default-getyourguide.webp';
	import { GetGetYourGuideToursStore } from '$houdini';
	import ErrorCard from '$lib/components/error/ErrorCard.svelte';
	import NotFound from '$lib/components/notFound/NotFound.svelte';
	import { buildReviewText } from '$lib/components/pois/content-utils';
	import GetYourGuideRecommendationCard from '$lib/components/recommendation/getyourguide/GetYourGuideRecommendationCard.svelte';
	import {
		type AugmentedHoudiniStore,
		augmentHoudiniStore,
	} from '$lib/utils/augmentedHoudiniStore';
	import * as m from '$paraglide/messages';

	interface Props {
		coordinates: Coordinates;
		onBookClick?: (getYourGuideTour: GetYourGuideTour) => void;
	}

	const { coordinates, onBookClick }: Props = $props();

	const queryStore: AugmentedHoudiniStore<GetGetYourGuideToursStore> = augmentHoudiniStore(new GetGetYourGuideToursStore());

	function fetchGetYourGuideTours({ lat, lng }: Coordinates) {
		queryStore.fetch({
			policy: 'NetworkOnly',
			variables: {
				lat,
				lng,
			},
		});
	}

	if (browser) {
		fetchGetYourGuideTours(coordinates);
	};
</script>

<div class="datasheet-recommendation w-full">
	<div class=" bg-neutral-weakest px-2 pb-2 md:pt-2 rd-2 md:shadow-smooth-block">
		<div class="text-5 mb-2">{m.poi_datasheet__recommendation__title()}</div>
		{#if $queryStore?.data?.getGetYourGuideTours.__typename === 'SearchGetYourGuideToursFound'}
			{#if $queryStore?.data?.getGetYourGuideTours?.tours?.length > 0}
				{#each $queryStore?.data?.getGetYourGuideTours?.tours as tour, index (tour)}
					<GetYourGuideRecommendationCard
						bookUrl={tour?.bookUrl}
						imageUrl={tour?.imageUrl ?? DefaultGetYourGuide}
						isLoading={$queryStore?.fetching}
						name={tour?.title}
						price={tour?.price}
						reviewText={buildReviewText(tour?.review)}
						class={index !== $queryStore?.data?.getGetYourGuideTours?.tours.length - 1
							? 'mb-4'
							: ''}
						onBookClick={() => onBookClick?.(tour)}
					/>
				{/each}
			{:else}
				<ErrorCard
					text={m.poi_datasheet__recommendation__getyourguide__tours_not_found()}
					errorImage={NoActivity}
					onRetry={() => fetchGetYourGuideTours(coordinates)}
				/>
			{/if}
		{:else if $queryStore?.data?.getGetYourGuideTours.__typename === 'SearchGetYourGuideToursNotFound'}
			<ErrorCard
				errorImage={NoActivity}
				text={m.poi_datasheet__recommendation__getyourguide__tours_not_found()}
				onRetry={() => fetchGetYourGuideTours(coordinates)}
			/>
		{:else if $queryStore?.data?.getGetYourGuideTours.__typename === 'SearchGetYourGuideToursNotFoundWithRegionData'}
			<NotFound
				text={m.poi_datasheet__recommendation__getyourguide__tours_not_found_with_region()}
				redirectUrl={$queryStore?.data?.getGetYourGuideTours?.url}
				label={m.poi_datasheet__recommendation__getyourguide__tour_not_found_with_region_label()}
				imageScr={NoActivity}
				imageAlt={m.poi_datasheet__recommendation__not_found_image_alt()}
			/>
		{:else if $queryStore?.data?.getGetYourGuideTours.__typename === 'SearchGetYourGuideToursNotFoundWithoutRegionData'}
			<NotFound
				text={m.poi_datasheet__recommendation__getyourguide__tours_not_found_without_region()}
				redirectUrl={$queryStore?.data?.getGetYourGuideTours.url}
				label={m.poi_datasheet__recommendation__getyourguide__tours_not_found_without_region_label()}
				imageScr={NoActivity}
				imageAlt={m.poi_datasheet__recommendation__not_found_image_alt()}
			/>
		{:else if $queryStore?.errors}
			<ErrorCard
				errorImage={NoActivity}
				text={m.poi_datasheet__recommendation__getyourguide__tours_not_found()}
				onRetry={() => fetchGetYourGuideTours(coordinates)}
			/>
		{:else if $queryStore?.fetching}
			{#each Array.from({ length: 3 }) as _, index (index)}
				<GetYourGuideRecommendationCard isLoading class="mb-4" />
			{/each}
		{/if}
	</div>
</div>
